import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Scale, Droplets, Globe2, Users, Flame,
  FlaskConical, ClipboardCheck, Calendar, Wrench, AlertTriangle, BookMarked,
  Gavel, ShieldAlert, HeartPulse, TrendingDown, FileCheck2, ArrowRight, ChevronDown,
} from "lucide-react";
import { COMPLIANCE, STANDARDS_BADGES } from "../lib/content";
import SEO from "../components/SEO";
import FAQ from "../components/FAQ";

const fadeUp = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.2, 0.7, 0.2, 1] } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };

// ──────────────────────────────────────────────────────────────────────
// COMPLIANCE GUIDE — 10 plain-English questions, written for the GM,
// the developer, the procurement officer, the body-corporate trustee.
// Same accordion behaviour as the buyer's guide on the tech pages.
// ──────────────────────────────────────────────────────────────────────
const COMPLIANCE_GUIDE = [
  ["What is water compliance — in one sentence?", "It is the legal, technical and documentary proof that the water you supply to anyone (guest, tenant, employee, customer or the public) is safe to drink, that you are allowed to take it from the source, and that any wastewater you discharge is within the limits the law sets. Three layers — safety, abstraction and discharge — each with its own paperwork."],
  ["Why does water compliance exist at all?", "Because contaminated water causes verifiable harm — gastrointestinal illness, heavy-metal poisoning, environmental damage, fish kills, foodborne outbreaks — and the polluter or supplier has to be findable, accountable and insurable. Without compliance there is no liability chain when something goes wrong; with it, every duty is named, dated, signed and traceable."],
  ["The moment I supply water to anyone other than my own family, what changes legally?", "You become a 'water services intermediary' under the Water Services Act 108 of 1997. That makes you legally responsible for the quality of every drop you supply — even on a borehole, even on a private estate, even on a lodge you own outright. Directors are personally liable in the case of a serious failure; ignorance of the duty is not a defence."],
  ["What's the difference between SANS 241, the Water Services Act and the National Water Act?", "SANS 241:2015 is the technical standard — the chemistry, the microbiology, the numeric limits your water must meet. The Water Services Act is the supply law — who is responsible for delivering safe water, what they must do, and what happens when they don't. The National Water Act governs source — when, how and how much water you may abstract from a borehole, river or dam, and what you must do with your discharge. Three different acts, three different duties, all live on every site that has water."],
  ["What does Blue Drop and Green Drop actually mean for a private site?", "Blue Drop is the Department of Water and Sanitation's public scorecard of drinking-water quality and management. Green Drop is the equivalent for wastewater. They are framed as a municipal score, but the methodology — risk assessment, sampling discipline, log-book quality, response time — is exactly what a lodge, an estate, a hotel or a mine should be measuring itself against if it wants insurer and auditor confidence. We design every HidroVerse plant against the Blue and Green Drop methodology, even where the scorecard does not legally apply."],
  ["What is the minimum a private site must do every month to be compliant?", "Pull a SANAS-accredited sample, log operator round-sheet readings, archive the lab certificate, raise a non-conformance report on anything that exceeded a SANS limit, lodge the corrective action, sign it off. That is the baseline duty — what we deliver on every site is more than that, but anything less and your file is not defensible."],
  ["How does HidroVerse assist with compliance — practically, not in marketing language?", "Three things. First, we design the plant so that the SANS 241 numbers are met without operator heroics — the chemistry works, not the prayer. Second, we run the monthly compliance programme — accredited sampling, lab arrangement, log-book audit, NCR and CAPA workflow, archive management. Third, when an auditor, inspector, insurer or DWS official arrives, we produce the file within an hour and we stand beside you while they read it. We do not just install equipment; we keep the paper trail alive."],
  ["What does a real audit actually look like — and what fails one?", "A typical audit asks for: the most recent 12 months of SANAS lab certificates, monthly operator round-sheets, the source authorisation or registration, the abstraction returns, the NCR/CAPA log, the chlorine and UV calibration records, and a maintenance schedule with completion sign-offs. Files fail when sampling is missed, when the lab is not SANAS-accredited, when corrective actions are open older than 30 days, when the abstraction return is overdue, or when the operator records are written-up after the fact. Each of these is a paper-trail failure, not a water-quality failure — and each is preventable on day one."],
  ["What is the actual cost of non-compliance — beyond fines?", "Insurance cover can be voided. A serious water-quality event can trigger directors' personal liability under the Companies Act. Reputational damage on TripAdvisor, Google or in industry press is uninsurable and lasts for years. A municipal failure-of-discharge notice can stop business operations until the issue is closed. The fine is rarely the real cost — the avoidable cost is the operational and reputational tail."],
  ["What is the first step to becoming compliant if I'm honest that I'm not today?", "A documented gap analysis. We come on site, sample the feed and the outlet, photograph the plant and the abstraction works, list every duty under WSA, NWA, SANS 241, SANS 10090 and your sector-specific obligations, and produce a single document that tells you exactly where you are non-compliant and exactly what each item costs to fix. No surprise findings six months in, no marketing-led upsells. You read the report, you decide what to fix and in what order, and you choose whether the remediation work, the monthly compliance programme, or both, are scoped to HidroVerse."],
];

// ──────────────────────────────────────────────────────────────────────
// FRAMEWORKS — expanded copy. Each card now reads as a paragraph, not a
// one-liner, so the operator/GM understands WHY each standard exists.
// ──────────────────────────────────────────────────────────────────────
const FRAMEWORKS = [
  {
    icon: Droplets, accent: "#1FB6C1", eyebrow: "SANS · Drinking water",
    title: "SANS 241:2015",
    subtitle: "The drinking-water specification.",
    body: "South Africa's national drinking-water specification. Sets numerical limits on microbiological, chemical, physical, aesthetic, operational and radiological determinands across two tables. Compliance is measured at the point of consumption (Section 5.6.2).",
    refs: [
      { label: "SANS 241:2015 · SABS store", url: "https://store.sabs.co.za/sans-241-1-2015" },
      { label: "SANS 241 revision · public technical committee", url: "https://www.umngeni-uthukela.co.za/wp-content/uploads/2024/07/SANS241ED7_TC147_DSS_2.pdf" },
    ],
    viewer: { label: "View full parameter list", to: "/compliance/sans-241-parameters" },
  },
  {
    icon: Scale, accent: "#1D6A8C", eyebrow: "Statute · Water supply",
    title: "Water Services Act 108 of 1997",
    subtitle: "Statutory duty of the intermediary.",
    body: "Section 1 defines a water services intermediary as any person obliged to provide water services under a contract where the water supply is incidental to the main object. Section 25 requires the intermediary to comply with the minimum standards prescribed by the Minister and any additional standards set by the water services authority. Section 82 provides criminal liability for wilful or negligent contravention.",
    refs: [
      { label: "WSA 108 of 1997 · SAFLII (consolidated)", url: "https://www.saflii.org/za/legis/consol_act/wsa1997175/" },
      { label: "Regulation R982 of 2001 · Compulsory National Standards", url: "https://www.gov.za/documents/water-services-act-compulsory-national-standards" },
    ],
  },
  {
    icon: Globe2, accent: "#42954B", eyebrow: "Statute · Water use",
    title: "National Water Act 36 of 1998",
    subtitle: "Authorisation of water use, abstraction and discharge.",
    body: "Section 21 lists eleven water uses (a–k), including abstraction, storage, waste discharge and stream-flow reduction. Section 22 prohibits any water use unless permissible under Schedule 1, an existing lawful use, a General Authorisation under Section 39, or a site-specific Water Use Licence. Section 151 sets out the criminal offences and penalties. HidroVerse advises on the applicable authorisation and prepares supporting technical documentation; registration, submission and record-keeping remain the responsibility of the water user.",
    refs: [
      { label: "NWA 36 of 1998 · full text (WRC)", url: "https://www.wrc.org.za/wp-content/uploads/mdocs/NWA%201998.pdf" },
      { label: "DWS · e-WULAAS licensing portal", url: "https://ewulaas.dws.gov.za/ewulaas/WUA.aspx" },
      { label: "DWS · authorised water uses register", url: "https://www.dws.gov.za/WAR/authorised.aspx" },
    ],
  },
  {
    icon: ShieldCheck, accent: "#0C2A40", eyebrow: "WHO · Drinking water",
    title: "WHO Guidelines for Drinking-water Quality",
    subtitle: "International benchmark referenced by SANS 241.",
    body: "The World Health Organisation Guidelines for Drinking-water Quality set health-based reference values used internationally. SANS 241:2015 Section 4 nominates the WHO Guidelines as the reference for any determinand not tabulated in the standard, once identified through a water-quality risk assessment.",
    refs: [
      { label: "WHO Guidelines · 4th ed. + amendments (PDF)", url: "https://iris.who.int/bitstream/handle/10665/254637/9789241549950-eng.pdf" },
      { label: "WHO · guidelines landing page", url: "https://www.who.int/publications/i/item/9789241549950" },
    ],
    viewer: { label: "View WHO reference values", to: "/compliance/who-parameters" },
  },
  {
    icon: HeartPulse, accent: "#B85C38", eyebrow: "Statute · Bathing water",
    title: "National Health Act 61 of 2003",
    subtitle: "Bathing and recreational-water quality.",
    body: "Swimming pools, spa pools and other bathing facilities are regulated separately from drinking water. Government Notice R.943 of 2013 issued under the National Health Act prescribes the National Environmental Health Norms and Standards, benchmarked against the WHO Guidelines for Safe Recreational Water Environments (Vol. 2, 2006). SANS 241 does not apply to bathing water.",
    refs: [
      { label: "GN R.943 of 2013 · National Environmental Health Norms", url: "https://www.gov.za/documents/national-health-act-national-environmental-health-norms-and-standards-premises-and" },
      { label: "WHO · Safe Recreational Water Environments (Vol. 2)", url: "https://www.who.int/publications/i/item/9241546808" },
    ],
  },
  {
    icon: FlaskConical, accent: "#1D6A8C", eyebrow: "Regulation · Discharge",
    title: "Government Notice 665 of 2013",
    subtitle: "General Authorisation for wastewater discharge.",
    body: "Published in Government Gazette 36820 under Section 39 of the National Water Act. Sets General and Special numerical limits for wastewater discharge to a water resource (COD, suspended solids, faecal coliforms, ammonia, nitrate, ortho-phosphate, pH). Discharges above 2 000 m³/day, or into a designated sensitive catchment, require a site-specific Water Use Licence.",
    refs: [
      { label: "GN 665 of 2013 · GG 36820 (DWS)", url: "https://www.dws.gov.za/documents/notices/GG_36820_665.pdf" },
      { label: "Effluent limits summary · academic review", url: "https://td-sa.net/index.php/td/article/view/581/933" },
    ],
  },
  {
    icon: Flame, accent: "#B85C38", eyebrow: "SANS · Fire water",
    title: "SANS 10090",
    subtitle: "Fire-suppression water reserves and pumps.",
    body: "Prescribes the design, installation and certification of fire-water reserves and pump sets for community and industrial protection. Certification by a competent person is a standing requirement of commercial property insurance in South Africa.",
    refs: [
      { label: "SANS 10090 · SABS store", url: "https://store.sabs.co.za/sans-10090-2018" },
    ],
  },
  {
    icon: Users, accent: "#1D6A8C", eyebrow: "Body · Professional",
    title: "Water Institute of Southern Africa (WISA)",
    subtitle: "Professional body for water engineering in South Africa.",
    body: "WISA is the professional body for water engineering, science and management in Southern Africa. HidroVerse holds active membership and participates in the CPD-accredited technical programme.",
    refs: [
      { label: "WISA · official site", url: "https://www.wisa.org.za/" },
    ],
  },
];

// ──────────────────────────────────────────────────────────────────────
// EVIDENCE PACK — the documents that constitute a defensible compliance
// file. Each item is cited to its source: statute, regulation, standard
// or gazetted General Authorisation. Where the interval is not fixed by
// a single statute (e.g. sampling cadence for smaller supply zones),
// SANS 241:2015 Annex B is cited as the reference practice.
// ──────────────────────────────────────────────────────────────────────
const EVIDENCE = [
  {
    icon: FlaskConical, accent: "#1FB6C1",
    eyebrow: "Per SANS 241 Annex B",
    title: "SANAS-accredited laboratory certificate",
    lede: "Microbiological and chemical analysis by a SANAS-accredited laboratory, sampled at the point of consumption. Sampling cadence follows the risk-based frequencies set out in SANS 241:2015 Annex B (informative); the WSA or catchment authority may prescribe a stricter interval. Records retained for a minimum of three years.",
    ref: "SANS 241:2015 Annex B · Regulation R982 of 2001 Section 5(2)",
  },
  {
    icon: ClipboardCheck, accent: "#1D6A8C",
    eyebrow: "Continuous",
    title: "Operator log of critical control points",
    lede: "Free chlorine residual, feed pressure, permeate flow, UV intensity and turbidity logged at the frequency defined by the plant's HACCP or water-safety plan. Constitutes the on-going monitoring required of a water services intermediary.",
    ref: "Water Services Act 108 of 1997 Section 25(1)(a) · SANS 241:2015 Section 4.2",
  },
  {
    icon: Wrench, accent: "#0C2A40",
    eyebrow: "Interval set by OHS / OEM",
    title: "Plant inspection and maintenance record",
    lede: "Mechanical, electrical and hydraulic inspection at intervals set by the Occupational Health and Safety Act General Machinery Regulations, the manufacturer's maintenance schedule and the property insurer's warranty conditions.",
    ref: "OHS Act 85 of 1993 · GMR reg. 2 · OEM schedule",
  },
  {
    icon: Calendar, accent: "#42954B",
    eyebrow: "Interval set by GA / WUL",
    title: "Water use return (abstraction)",
    lede: "Volumetric return of water taken from a water resource, submitted to the Department of Water and Sanitation via the e-WULAAS portal on the interval prescribed in the applicable General Authorisation or Water Use Licence (typically monthly, quarterly or annually).",
    ref: "National Water Act 36 of 1998 Section 26 & Section 35 · GA / WUL conditions",
  },
  {
    icon: FlaskConical, accent: "#1D6A8C",
    eyebrow: "Interval set by GA / WUL",
    title: "Effluent discharge compliance sample",
    lede: "Wastewater analysis against the General Limit or Special Limit values, submitted with the discharge return to DWS. The sampling interval and analyte list are prescribed in the General Authorisation or the Water Use Licence for the site.",
    ref: "GN 665 of 2013 (GG 36820) · NWA Section 21(f)/(g)/(h)",
  },
  {
    icon: Flame, accent: "#B85C38",
    eyebrow: "Weekly test · annual certification",
    title: "Fire-water reserve certification",
    lede: "Weekly test of the fire-water reserve and pump set logged on premises; annual certification by a competent person issued to the insurer.",
    ref: "SANS 10090 · insurance policy warranty",
  },
  {
    icon: AlertTriangle, accent: "#B85C38",
    eyebrow: "On exceedance",
    title: "Non-conformance and corrective-action record",
    lede: "Every determinand exceeding a numerical limit — drinking, bathing or effluent — must be recorded with root-cause analysis, corrective action, verification and closure. The record is the primary artefact requested in an audit.",
    ref: "Regulation R982 of 2001 Section 5(3) · SANS 241:2015 Section 5.5",
  },
  {
    icon: BookMarked, accent: "#1FB6C1",
    eyebrow: "On appointment",
    title: "Operator training record",
    lede: "Documented training and competency assessment for every operator, technician and maintenance contractor working on the water services works.",
    ref: "OHS Act 85 of 1993 Section 8 · General Safety Regulations",
  },
];

export default function Compliance() {
  return (
    <div data-testid="compliance-page">
      <style>{`
        details[data-collapse] > summary { list-style: none; cursor: pointer; }
        details[data-collapse] > summary::-webkit-details-marker { display: none; }
        details[data-collapse] .lc-arrow { transition: transform 220ms ease; }
        details[data-collapse][open] .lc-arrow { transform: rotate(180deg); }
        details[data-collapse][open] > summary { border-bottom-left-radius: 0; border-bottom-right-radius: 0; }
        details[data-collapse] .lc-body { animation: lcReveal 260ms ease both; }
        @keyframes lcReveal { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <SEO
        title="Compliance"
        description="What water compliance really means for a commercial site in South Africa — and how HidroVerse keeps your site documented, defensible and audit-ready, every month."
        path="/compliance"
      />

      {/* HERO — lab + checklist imagery, no eyebrow */}
      <section className="eng-hero">
        <div className="eh-bg" aria-hidden>
          <img src="/generated/hero-compliance.webp" alt="" loading="lazy" decoding="async"/>
        </div>
        <div className="eh-veil" aria-hidden />
        <div className="eh-content">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.h1 variants={fadeUp}>
              Water<br />
              <strong>compliance.</strong>
            </motion.h1>
            <motion.p variants={fadeUp} className="eh-lede">
              Providing water to another carries the responsibility to comply with the
              governing laws in your area. Find out how to comply — and how HidroVerse
              can assist.
            </motion.p>
            <motion.div variants={fadeUp} className="eh-actions">
              <Link to="/contact" className="hv-magnetic">Contact us <span aria-hidden>→</span></Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* WATER SERVICES INTERMEDIARY — the legal test.
          The moment-of-realisation section. Plain English + statute. */}
      <section
        className="px-6 md:px-12 lg:px-20 py-14 md:py-20 border-b"
        style={{ background: "#FFFFFF", color: "#0C2A40", borderColor: "rgba(12,42,64,0.08)" }}
        data-testid="compliance-wsi-test"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-5">
              <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5"
                style={{
                  background: "rgba(184,92,56,0.10)",
                  border: "1px solid rgba(184,92,56,0.28)",
                  color: "#B85C38",
                  fontSize: "0.64rem",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  borderRadius: 999,
                }}
              >
                <Gavel size={12} aria-hidden /> The legal test · read this first
              </div>
              <h2 className="font-display" style={{
                fontSize: "clamp(1.7rem, 3.4vw, 2.7rem)",
                lineHeight: 1.1, letterSpacing: "-0.018em", fontWeight: 500, color: "#0C2A40",
              }}>
                Are you a Water<br />
                Services Intermediary?
              </h2>
              <p className="mt-5 font-light leading-relaxed" style={{ fontSize: "1rem", color: "rgba(26,31,37,0.75)", maxWidth: "48ch" }}>
                If any of the answers below is <strong style={{ color: "#B85C38" }}>yes</strong>, you are — in the eyes of South African
                law — a Water Services Intermediary. That is not a title you apply for.
                It attaches automatically to the person or company supplying the water,
                the moment the supply begins.
              </p>
              <p className="mt-4 font-mono uppercase" style={{ fontSize: "0.68rem", letterSpacing: "0.16em", color: "rgba(12,42,64,0.55)" }}>
                Water Services Act 108 of 1997 · Section 1 (definitions), Section 25 (duty of intermediary)
              </p>
            </div>

            <div className="lg:col-span-7">
              <ul className="grid sm:grid-cols-2 gap-3" data-testid="compliance-wsi-questions">
                {[
                  "You run a lodge, hotel, guesthouse or camp and supply water to guests for any use — drinking, brushing teeth, showering, food preparation or ice.",
                  "You employ staff on site and supply water to their village, canteen or ablutions.",
                  "You run a body corporate, estate, complex or retirement village on borehole or reticulated feed.",
                  "You are a farm, mine, factory or processing plant with a staff canteen or wash-up water.",
                  "You are a school, clinic, hospital or crèche supplying water on premises.",
                  "You are a landlord supplying water to any tenant — commercial or residential.",
                  "You are a municipality, WSA sub-contractor, or private utility.",
                  "You sell bottled or bulk water to any third party.",
                ].map((t, i) => (
                  <li key={i}
                    className="flex items-start gap-3 p-4"
                    style={{
                      background: "#FAFAF7",
                      border: "1px solid rgba(12,42,64,0.08)",
                      borderLeft: "3px solid #B85C38",
                      borderRadius: 4,
                    }}
                  >
                    <span
                      className="font-mono shrink-0 mt-0.5"
                      style={{
                        width: 22, height: 22, display: "inline-flex",
                        alignItems: "center", justifyContent: "center",
                        background: "rgba(184,92,56,0.10)", color: "#B85C38",
                        fontSize: "0.7rem", fontWeight: 700, borderRadius: 4,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span style={{ fontSize: "0.94rem", color: "#0C2A40", lineHeight: 1.5 }}>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Full-width drinking-water definition callout — sits between the grid and the navy statute block */}
          <div
            className="mt-10 p-5 md:p-6"
            style={{
              background: "rgba(184,92,56,0.06)",
              border: "1px solid rgba(184,92,56,0.35)",
              borderLeft: "4px solid #B85C38",
              borderRadius: 4,
            }}
            data-testid="compliance-drinking-water-definition"
          >
            <div
              className="font-mono uppercase mb-2"
              style={{ fontSize: "0.62rem", letterSpacing: "0.22em", color: "#B85C38", fontWeight: 700 }}
            >
              Before you read on · what &ldquo;drinking water&rdquo; means in law
            </div>
            <p className="font-light leading-relaxed" style={{ fontSize: "0.98rem", color: "#1A1F25" }}>
              &ldquo;Drinking water&rdquo; in South African law is defined by the outlet
              and by the point of consumption &mdash; not by whether a guest
              chooses to drink from it. Regulation R509 of 2001 (issued under
              the Water Services Act) defines potable water as water intended
              for &ldquo;human ingestion, food preparation, dental hygiene or
              personal hygiene&rdquo;. SANS 241:2015 sets its compliance point
              at &ldquo;the point at which the water is used or consumed&rdquo;
              (Section 5.6.2), which includes the kitchen tap, the ice
              machine, the villa basin, the shower head, the bar tap and any
              other outlet a person can put in contact with their mouth, skin
              or food. Brushing teeth, showering, rinsing salad, filling a
              kettle, making ice or refilling a bedside carafe all fall
              inside that definition. &ldquo;They do not drink the water&rdquo;
              is not a legal defence.
            </p>
          </div>

          {/* Full-width navy statute block — no "verbatim" heading, quote + reference only */}
          <div
            className="mt-6 p-6 md:p-8"
            style={{
              background: "#0C2A40",
              color: "#F5F7FA",
              borderRadius: 4,
            }}
            data-testid="compliance-wsi-statute-quote"
          >
            <p className="font-light italic leading-relaxed" style={{ fontSize: "1.05rem", color: "rgba(245,247,250,0.92)" }}>
              &ldquo;The quality, quantity and sustainability of water services provided
              by a water services intermediary must meet any minimum standards
              prescribed by the Minister &hellip; and any additional minimum
              standards set by the water services authority having jurisdiction
              in the area in question.&rdquo;
            </p>
            <p className="mt-4 font-mono uppercase" style={{ fontSize: "0.66rem", letterSpacing: "0.2em", color: "rgba(245,247,250,0.6)" }}>
              — Water Services Act 108 of 1997, Section 25(1)(a)
            </p>
          </div>
        </div>
      </section>

      {/* EXPOSURE — what non-compliance actually costs, not scare stories */}
      <section
        className="px-6 md:px-12 lg:px-20 py-14 md:py-20"
        style={{ background: "#FAFAF7", color: "#0C2A40" }}
        data-testid="compliance-exposure"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5"
            style={{
              background: "rgba(12,42,64,0.06)",
              border: "1px solid rgba(12,42,64,0.14)",
              color: "#0C2A40",
              fontSize: "0.64rem", letterSpacing: "0.22em",
              textTransform: "uppercase", fontWeight: 700, borderRadius: 999,
            }}
          >
            <ShieldAlert size={12} aria-hidden style={{ color: "#B85C38" }} /> Legal &amp; commercial exposure
          </div>
          <h2 className="font-display mb-4 max-w-3xl" style={{
            fontSize: "clamp(1.7rem, 3.2vw, 2.6rem)",
            lineHeight: 1.15, letterSpacing: "-0.015em", fontWeight: 500, color: "#0C2A40",
          }}>
            The cost of non-compliance.
          </h2>
          <p className="font-light leading-relaxed mb-10 max-w-3xl" style={{ fontSize: "1rem", color: "rgba(26,31,37,0.72)" }}>
            Regulators do issue fines — but the fine is rarely what breaks a
            commercial operation. The real exposure sits in the four consequences
            below. Each one activates automatically, without a court date, the
            moment a serious water event happens and your evidence file is not
            defensible.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: Gavel,
                title: "Directors' personal liability",
                body: "Under Section 77 of the Companies Act 71 of 2008, directors are personally liable for loss caused by any act or omission — including failure of statutory duty. A Water Services Intermediary duty is a statutory duty.",
                ref: "Companies Act 71 of 2008 · Section 77(3)(b)",
              },
              {
                icon: ShieldAlert,
                title: "Insurance cover void",
                body: "Every public-liability policy on a commercial site carries a statutory-compliance warranty. A water-quality event on a non-compliant site gives the insurer legal grounds to refuse indemnity — you carry the claim yourself.",
                ref: "Insurance industry practice · SAIA guideline",
              },
              {
                icon: HeartPulse,
                title: "Criminal liability on harm",
                body: "Under Section 82 of the Water Services Act, wilful or negligent failure to comply is a criminal offence — fine or imprisonment. If contaminated water causes illness or injury, culpable homicide and common-law negligence also come on the table. The operator, the manager and the responsible director are all in the frame.",
                ref: "Water Services Act 108 of 1997 · Section 82",
              },
              {
                icon: TrendingDown,
                title: "Operational shutdown",
                body: "DWS, the Water Services Authority (municipality) and environmental-health inspectors have direct powers to issue a directive requiring the operation to stop until a specified defect is corrected. Trading stops the day the notice is served, not the day the appeal is heard.",
                ref: "National Water Act 36 of 1998 · Section 19; WSA Section 82",
              },
            ].map(({ icon: Icon, title, body, ref }) => (
              <div key={title}
                className="p-5 flex flex-col"
                style={{
                  background: "#FFFFFF",
                  border: "1px solid rgba(12,42,64,0.10)",
                  borderTop: "3px solid #B85C38",
                  borderRadius: 4,
                }}
              >
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center mb-4"
                  style={{ background: "rgba(184,92,56,0.10)", color: "#B85C38" }}
                >
                  <Icon size={18} aria-hidden />
                </div>
                <div className="font-display mb-2" style={{ fontSize: "1.02rem", fontWeight: 700, letterSpacing: "-0.005em", color: "#0C2A40" }}>
                  {title}
                </div>
                <p className="font-light leading-relaxed" style={{ fontSize: "0.9rem", color: "rgba(26,31,37,0.72)" }}>
                  {body}
                </p>
                <div className="mt-4 pt-3 font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.16em", color: "rgba(12,42,64,0.5)", borderTop: "1px solid rgba(12,42,64,0.08)" }}>
                  {ref}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THREE PILLARS OF WATER COMPLIANCE — formal, bullet-based reference. */}
      <section
        className="px-6 md:px-12 lg:px-20 pt-8 md:pt-10 pb-14 md:pb-20"
        style={{ background: "#FAFAF7", color: "#0C2A40" }}
        data-testid="compliance-explainer"
      >
        <div className="max-w-[1300px] mx-auto">
          <h2
            className="font-display mb-4"
            style={{ fontSize: "clamp(1.7rem, 3.2vw, 2.6rem)", lineHeight: 1.15, letterSpacing: "-0.015em", fontWeight: 500 }}
          >
            The three pillars of water compliance.
          </h2>
          <p
            className="font-light leading-relaxed mb-10 max-w-3xl"
            style={{ fontSize: "0.98rem", color: "rgba(26,31,37,0.72)" }}
          >
            South African water compliance rests on three statutory pillars.
            Each pillar has its own governing instrument, its own regulator and
            its own evidence obligation. A defensible file must address all
            three concurrently.
          </p>

          <div className="grid lg:grid-cols-3 gap-4" data-testid="compliance-pillars">
            {[
              {
                n: "01",
                title: "Abstraction",
                subtitle: "Authorisation to take water from a resource.",
                tint: "#42954B",
                defn:
                  "The act of taking water from a borehole, surface watercourse, dam, spring or catchment. Regulated as a water use under the National Water Act 36 of 1998.",
                where: [
                  "National Water Act 36 of 1998, Section 21(a), Section 21(b), Section 22, Section 39",
                  "General Authorisations in force (published in the Government Gazette)",
                  "Site-specific Water Use Licence (WUL) where thresholds are exceeded",
                ],
                how: [
                  "Register the use on the DWS e-WULAAS portal",
                  "Install a functional volumetric meter at each abstraction point",
                  "Log monthly abstraction readings against the authorised volume",
                  "Submit statutory returns on the interval prescribed in the GA / WUL",
                  "Renew or amend the authorisation within the validity period",
                ],
                refs: [
                  { label: "NWA 36 of 1998", url: "https://www.wrc.org.za/wp-content/uploads/mdocs/NWA%201998.pdf" },
                  { label: "DWS · e-WULAAS", url: "https://ewulaas.dws.gov.za/ewulaas/WUA.aspx" },
                  { label: "DWS authorised uses register", url: "https://www.dws.gov.za/WAR/authorised.aspx" },
                ],
              },
              {
                n: "02",
                title: "Safety &amp; purification",
                subtitle: "Water fit for the purpose it is put to.",
                tint: "#1FB6C1",
                defn:
                  "The obligation to supply water that meets prescribed quality standards at the point of use — drinking, cooking, brushing and showering are all judged against SANS 241:2015; bathing and pool water against a separate regime.",
                where: [
                  "Water Services Act 108 of 1997, Section 25 (duty of intermediary)",
                  "Regulation R982 of 2001 — Compulsory National Standards",
                  "SANS 241:2015 — drinking-water specification",
                  "National Health Act 61 of 2003 · GN R.943 of 2013 (pools and recreational water)",
                ],
                how: [
                  "Sample at the point of consumption, not the plant outlet (SANS 241:2015 Section 5.6.2)",
                  "Monthly microbiological analysis at four or more representative outlets",
                  "Quarterly operational chemistry (free chlorine, conductivity, pH, turbidity)",
                  "Annual full SANS 241 panel including heavy metals, nitrate, fluoride, DBPs",
                  "SANAS-accredited laboratory only; retain results for at least three years (R982 of 2001)",
                  "Daily operator log for each pool: chlorine, pH, turbidity",
                ],
                refs: [
                  { label: "WSA 108 of 1997", url: "https://www.saflii.org/za/legis/consol_act/wsa1997175/" },
                  { label: "SANS 241:2015 · SABS", url: "https://www.store.sabs.co.za/sans-241-1-2015" },
                  { label: "R982 of 2001 · DWS", url: "https://www.gov.za/documents/water-services-act-compulsory-national-standards" },
                  { label: "GN R.943 of 2013 · NHA", url: "https://www.gov.za/documents/national-health-act-national-environmental-health-norms-and-standards-premises-and" },
                ],
              },
              {
                n: "03",
                title: "Discharge",
                subtitle: "Wastewater returned to the environment.",
                tint: "#1D6A8C",
                defn:
                  "The act of discharging waste, or water containing waste, into a water resource. Regulated as a distinct water use, with numerical effluent limits.",
                where: [
                  "National Water Act 36 of 1998, Section 21(f), Section 21(g), Section 21(h)",
                  "Government Notice 665 of 2013 (GG 36820) — General Authorisation for discharge",
                  "Site-specific Water Use Licence where the discharge exceeds 2 000 m³/day or the catchment is designated sensitive",
                ],
                how: [
                  "Sample the final effluent monthly against the applicable General or Special Limit",
                  "Submit volumetric and quality returns via e-WULAAS on the prescribed interval",
                  "Maintain a sludge disposal manifest for every removal",
                  "Log calibration and maintenance of dosing, aeration and disinfection equipment",
                  "Raise a non-conformance and corrective-action record on every out-of-limit result",
                ],
                refs: [
                  { label: "GN 665 of 2013 · GG 36820", url: "https://www.dws.gov.za/documents/notices/GG_36820_665.pdf" },
                  { label: "NWA Section 21 water uses", url: "https://www.wrc.org.za/wp-content/uploads/mdocs/NWA%201998.pdf" },
                  { label: "DWS · e-WULAAS", url: "https://ewulaas.dws.gov.za/ewulaas/WUA.aspx" },
                ],
              },
            ].map((p) => (
              <article
                key={p.n}
                className="p-6 flex flex-col"
                style={{
                  background: "#FFFFFF",
                  border: "1px solid rgba(12,42,64,0.10)",
                  borderTop: `3px solid ${p.tint}`,
                  borderRadius: 4,
                }}
                data-testid={`compliance-pillar-${p.n}`}
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span
                    className="font-mono"
                    style={{ fontSize: "1.6rem", fontWeight: 700, color: p.tint, letterSpacing: "-0.02em", lineHeight: 1 }}
                  >
                    {p.n}
                  </span>
                  <span
                    className="font-mono uppercase"
                    style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: p.tint, fontWeight: 700 }}
                  >
                    Pillar
                  </span>
                </div>
                <h3
                  className="font-display"
                  style={{ fontSize: "1.2rem", fontWeight: 700, letterSpacing: "-0.008em", color: "#0C2A40" }}
                  dangerouslySetInnerHTML={{ __html: p.title }}
                />
                <p
                  className="font-display italic mt-1 mb-4"
                  style={{ fontSize: "0.92rem", color: p.tint, fontWeight: 500 }}
                >
                  {p.subtitle}
                </p>
                <p
                  className="font-light leading-relaxed mb-5"
                  style={{ fontSize: "0.92rem", color: "rgba(26,31,37,0.75)" }}
                >
                  {p.defn}
                </p>

                <PillarList label="Where to look — governing instruments" items={p.where} tint={p.tint} />
                <PillarList label="How to comply — operational duties" items={p.how} tint={p.tint} />

                <div
                  className="mt-auto pt-4"
                  style={{ borderTop: "1px dashed rgba(12,42,64,0.14)" }}
                >
                  <div
                    className="font-mono uppercase mb-2"
                    style={{ fontSize: "0.56rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.55)", fontWeight: 700 }}
                  >
                    References
                  </div>
                  <ul className="space-y-1.5">
                    {p.refs.map((r) => (
                      <li key={r.url}>
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono inline-flex items-center gap-1.5 hover:underline"
                          style={{ fontSize: "0.72rem", color: p.tint, letterSpacing: "0.02em" }}
                        >
                          {r.label} <ArrowRight size={10} aria-hidden />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* THE LAW · VERBATIM — statute quotes with proper citation */}
      <section
        className="px-6 md:px-12 lg:px-20 pt-8 md:pt-10 pb-14 md:pb-20"
        style={{ background: "#FFFFFF", color: "#0C2A40" }}
        data-testid="compliance-statute-quotes"
      >
        <div className="max-w-[1400px] mx-auto">
          <h2 className="font-display mb-4 max-w-3xl" style={{
            fontSize: "clamp(1.7rem, 3.2vw, 2.6rem)",
            lineHeight: 1.15, letterSpacing: "-0.015em", fontWeight: 500, color: "#0C2A40",
          }}>
            What the law says.
          </h2>
          <p className="font-light leading-relaxed mb-10 max-w-3xl" style={{ fontSize: "1rem", color: "rgba(26,31,37,0.72)" }}>
            South African water compliance rests on five instruments — three
            statutes, one standard, one guideline. The clauses below are the ones
            an auditor, an insurer, a DWS inspector or a plaintiff&rsquo;s attorney will
            open first.
          </p>

          <div className="grid md:grid-cols-2 gap-5" data-testid="compliance-statute-list">
            {[
              {
                act: "Water Services Act 108 of 1997",
                ref: "Section 1 — definitions",
                quote:
                  "&ldquo;water services intermediary&rdquo; means any person who is obliged to provide water services to another in terms of a contract where the obligation to provide water services is incidental to the main object of that contract.",
                plain:
                  "Selling accommodation, employing staff, leasing property — the moment supplying water is 'incidental' to your main business, you are an intermediary.",
              },
              {
                act: "Water Services Act 108 of 1997",
                ref: "Section 23 — duties of the intermediary",
                quote:
                  "Every water services intermediary must ensure the efficient, affordable, economical and sustainable provision of water services &hellip; and that any water services provider providing water services on its behalf complies with the provisions of this Act and any applicable norms and standards prescribed under this Act.",
                plain:
                  "The duty is active and non-delegable. Contracting a plumber or a water company does not shift it — the intermediary remains legally answerable.",
              },
              {
                act: "Water Services Act 108 of 1997",
                ref: "Section 82 — offences",
                quote:
                  "A person who wilfully or negligently fails to comply with any provision of this Act or of any regulation made under this Act is guilty of an offence &hellip; and liable, on conviction, to a fine or to imprisonment.",
                plain:
                  "Non-compliance is not a civil regret. Under Section 82, wilful or negligent failure is a criminal offence — the natural persons in control of the site are within scope.",
              },
              {
                act: "Regulation R982 of 2001",
                ref: "Compulsory National Standards",
                quote:
                  "A water services institution must monitor the quality of potable water provided to consumers on an on-going basis, keep records of the results of that monitoring for at least three years, and make those records available on request.",
                plain:
                  "This is the regulation Section 23 of the WSA points to. Ongoing monitoring · three-year retention · accessible on request — the bare-minimum evidence footprint.",
              },
              {
                act: "National Water Act 36 of 1998",
                ref: "Section 21 — water uses requiring authorisation",
                quote:
                  "Water use includes — taking water from a water resource; storing water; disposing of waste in a manner which may detrimentally impact on a water resource &hellip; and no person may use water otherwise than as permitted under this Act.",
                plain:
                  "Every borehole, river intake, dam, spring — and every effluent discharge — needs either General Authorisation registration or a site-specific Water Use Licence.",
              },
              {
                act: "National Water Act 36 of 1998",
                ref: "Section 26 & Section 35 — quarterly returns",
                quote:
                  "The Minister may require any water user to register a water use and to submit returns to the responsible authority in the form and at the intervals prescribed.",
                plain:
                  "Registration is not one-off. Volumetric returns are mandatory on the interval set out in your General Authorisation or Water Use Licence.",
              },
              {
                act: "SANS 241:2015",
                ref: "Clause 5 & Annex A · numeric limits",
                quote:
                  "Drinking water shall not contain any determinand in concentrations that exceed the numerical limits given in Table 1 for micro-organisms and Table 2 for chemical determinands.",
                plain:
                  "SANS 241 defines acute-health, chronic-health, aesthetic and operational classes — twelve determinand classes in total, not one. Annual bacteriology only covers one corner of one class.",
              },
              {
                act: "SANS 241:2015",
                ref: "Section 5.6.2 — point of compliance",
                quote:
                  "The point at which drinking water shall comply with the numerical limits of this standard is the point at which the water is used or consumed &hellip; not the point at which it is treated or supplied.",
                plain:
                  "The kitchen tap, the suite basin, the drinking fountain — that is the audit point. Free chlorine that leaves the plant at 0.6 mg/L can sit at 0.18 mg/L at the far villa after a summer reticulation. The plant may pass; the villa fails; the audit names the villa.",
              },
              {
                act: "Companies Act 71 of 2008",
                ref: "Section 77(3)(b) — director liability",
                quote:
                  "A director of a company is liable for any loss, damages or costs sustained by the company as a direct or indirect consequence of the director having acquiesced in the carrying on of the company&rsquo;s business despite knowing that it was being conducted in contravention of any law.",
                plain:
                  "Contravening the WSA or NWA is 'contravention of a law'. The director does not have to have caused the harm — 'acquiescence' with knowledge is enough.",
              },
            ].map((s) => (
              <div key={s.ref + s.act}
                className="p-6"
                style={{
                  background: "#FAFAF7",
                  border: "1px solid rgba(12,42,64,0.08)",
                  borderLeft: "3px solid #1FB6C1",
                  borderRadius: 4,
                }}
              >
                <div className="flex items-baseline justify-between gap-4 mb-3 flex-wrap">
                  <div className="font-display" style={{ fontSize: "0.96rem", fontWeight: 700, color: "#0C2A40", letterSpacing: "-0.005em" }}>
                    {s.act}
                  </div>
                  <div className="font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.16em", color: "#1FB6C1", fontWeight: 700 }}>
                    {s.ref}
                  </div>
                </div>
                <p className="font-light italic leading-relaxed" style={{ fontSize: "0.94rem", color: "#1A1F25" }}
                  dangerouslySetInnerHTML={{ __html: s.quote }}
                />
                <div className="mt-3 pt-3 font-light" style={{ fontSize: "0.86rem", color: "rgba(26,31,37,0.65)", borderTop: "1px dashed rgba(12,42,64,0.14)" }}>
                  <span className="font-mono uppercase mr-2" style={{ fontSize: "0.6rem", letterSpacing: "0.18em", color: "rgba(12,42,64,0.45)" }}>
                    Plain English
                  </span>
                  {s.plain}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 font-light" style={{ fontSize: "0.86rem", color: "rgba(26,31,37,0.55)" }}>
            Instruments cited: Water Services Act 108 of 1997 · National Water Act
            36 of 1998 · Companies Act 71 of 2008 · Regulation R982 of 2001
            (Compulsory National Standards) · SANS 241:2015 (Drinking Water)
            · SANS 10090 (Community Protection Against Fire). WHO Guidelines for
            Drinking-water Quality (4th ed., inc. amendments) apply as international
            benchmark. Positions on Blue &amp; Green Drop practice draw from the
            Department of Water &amp; Sanitation methodology and Water Institute of
            Southern Africa (WISA) technical guidance.
          </p>
        </div>
      </section>

      {/* FRAMEWORKS — expanded tiles, bolder titles */}
      <section className="bg-white px-6 md:px-12 lg:px-20 pt-8 md:pt-10 pb-14 md:pb-20" data-testid="frameworks">
        <div className="max-w-[1500px] mx-auto">
          <h2 className="font-display mb-3" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)", lineHeight: 1.15, letterSpacing: "-0.015em", fontWeight: 500, color: "#0C2A40" }}>
            Water compliance standards.
          </h2>
          <p className="font-light leading-relaxed mb-10 max-w-3xl" style={{ fontSize: "0.98rem", color: "rgba(26,31,37,0.7)" }}>
            The statutes, regulations and technical standards that govern
            drinking, bathing, discharge and abstraction on a South African
            commercial site. Each block links to the primary source document.
          </p>
          <div className="space-y-3" data-testid="frameworks-banners">
            {FRAMEWORKS.map((f) => {
              const Icon = f.icon;
              return (
                <details key={f.title} data-collapse data-testid={`framework-${f.title.replace(/\s+/g, "-").toLowerCase()}`}>
                  <summary
                    className="flex items-center gap-4 px-5 py-4 md:px-6 md:py-5"
                    style={{
                      background: "#FAFAF7",
                      border: "1px solid rgba(12,42,64,0.10)",
                      borderLeft: `4px solid ${f.accent}`,
                      borderRadius: 4,
                    }}
                  >
                    <span
                      className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                      style={{ background: `${f.accent}1F`, color: f.accent }}
                    >
                      <Icon size={16} aria-hidden />
                    </span>
                    <span className="font-mono uppercase hidden md:inline-block md:w-40" style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: f.accent, fontWeight: 700, flexShrink: 0 }}>
                      {f.eyebrow}
                    </span>
                    <span className="hidden md:inline" style={{ color: "rgba(12,42,64,0.25)", flexShrink: 0 }}>·</span>
                    <span className="font-display flex-1 md:flex-none md:w-80" style={{ fontSize: "1.02rem", fontWeight: 700, color: "#0C2A40", letterSpacing: "-0.005em" }}>
                      {f.title}
                    </span>
                    <span className="hidden md:inline" style={{ color: "rgba(12,42,64,0.25)", flexShrink: 0 }}>·</span>
                    <span className="hidden md:inline font-display flex-1" style={{ fontSize: "0.94rem", fontStyle: "italic", color: f.accent, fontWeight: 500 }}>
                      {f.subtitle}
                    </span>
                    <span className="md:hidden flex-1" />
                    <ChevronDown className="lc-arrow" size={18} style={{ color: f.accent, flexShrink: 0 }} aria-hidden />
                  </summary>
                  <div
                    className="lc-body p-5 md:p-6"
                    style={{
                      background: "#FAFAF7",
                      border: "1px solid rgba(12,42,64,0.10)",
                      borderLeft: `4px solid ${f.accent}`,
                      borderTop: "none",
                      borderBottomLeftRadius: 4,
                      borderBottomRightRadius: 4,
                    }}
                  >
                    <div className="md:hidden mb-3 font-display" style={{ fontSize: "0.94rem", fontStyle: "italic", color: f.accent, fontWeight: 500 }}>
                      {f.subtitle}
                    </div>
                    <p className="font-light leading-relaxed" style={{ fontSize: "0.94rem", color: "rgba(26,31,37,0.78)", maxWidth: "80ch" }}>
                      {f.body}
                    </p>

                    {(f.refs || f.viewer) && (
                      <div className="mt-4 pt-4" style={{ borderTop: "1px dashed rgba(12,42,64,0.14)" }}>
                        {f.refs && (
                          <>
                            <div
                              className="font-mono uppercase mb-2"
                              style={{ fontSize: "0.56rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.55)", fontWeight: 700 }}
                            >
                              References
                            </div>
                            <ul className="space-y-1.5 mb-3">
                              {f.refs.map((r) => (
                                <li key={r.url}>
                                  <a
                                    href={r.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-mono inline-flex items-center gap-1.5 hover:underline"
                                    style={{ fontSize: "0.72rem", color: f.accent, letterSpacing: "0.02em" }}
                                    data-testid={`framework-ref-${r.label.replace(/\s+/g, "-").toLowerCase()}`}
                                  >
                                    {r.label} <ArrowRight size={10} aria-hidden />
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                        {f.viewer && (
                          <Link
                            to={f.viewer.to}
                            className="inline-flex items-center gap-2 px-4 py-2 font-mono uppercase"
                            style={{
                              fontSize: "0.62rem",
                              letterSpacing: "0.18em",
                              color: "#FFFFFF",
                              background: f.accent,
                              borderRadius: 4,
                              fontWeight: 700,
                            }}
                            data-testid={`framework-viewer-${f.viewer.to.split("/").pop()}`}
                          >
                            {f.viewer.label} <ArrowRight size={12} aria-hidden />
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        </div>
      </section>

      {/* THE COMPLIANCE LIFECYCLE — sequential 3-stage story.
          Abstraction → Use → Wastewater. Every lodge / estate / mine flows
          through all three. Each stage: what it is, the SA law that applies,
          what you must do, what happens if you don't.                       */}
      <section
        className="px-6 md:px-12 lg:px-20 pt-14 md:pt-20 pb-8 md:pb-10"
        style={{ background: "#FFFFFF", color: "#0C2A40" }}
        data-testid="compliance-lifecycle"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5"
            style={{
              background: "rgba(31,182,193,0.10)",
              border: "1px solid rgba(31,182,193,0.28)",
              color: "#0C2A40",
              fontSize: "0.64rem", letterSpacing: "0.22em",
              textTransform: "uppercase", fontWeight: 700, borderRadius: 999,
            }}
          >
            <Droplets size={12} aria-hidden style={{ color: "#1FB6C1" }} /> The compliance lifecycle · source to discharge
          </div>
          <p className="font-light leading-relaxed mb-10" style={{ fontSize: "1rem", color: "rgba(26,31,37,0.72)" }}>
            Water on any commercial South African site passes through the same
            three regulatory stages: you <strong>abstract</strong> it, you{" "}
            <strong>use</strong> it, and eventually you <strong>discharge</strong> it.
            Each stage has its own statute, its own regulator and its own
            evidence requirement. Passing one does not discharge the others —
            an audit that names your borehole registration does not care
            whether your tap water is safe, and vice versa.
          </p>

          <div className="space-y-4" data-testid="compliance-lifecycle-stages">

            {/* STAGE 1 — ABSTRACTION (collapsible banner) */}
            <details data-collapse data-testid="lifecycle-stage-abstraction">
              <summary
                className="flex items-center gap-4 px-5 py-4 md:px-6 md:py-5"
                style={{
                  background: "#FAFAF7",
                  border: "1px solid rgba(12,42,64,0.10)",
                  borderLeft: "4px solid #42954B",
                  borderRadius: 4,
                }}
              >
                <span className="font-mono" style={{ fontSize: "1.3rem", fontWeight: 700, color: "#42954B", letterSpacing: "-0.02em", lineHeight: 1, flexShrink: 0 }}>
                  01
                </span>
                <span className="font-mono uppercase hidden md:inline-block md:w-36" style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "#42954B", fontWeight: 700, flexShrink: 0 }}>
                  Stage · abstraction
                </span>
                <span className="hidden md:inline" style={{ color: "rgba(12,42,64,0.25)" }}>·</span>
                <span className="font-display flex-1" style={{ fontSize: "1.02rem", fontWeight: 600, color: "#0C2A40", letterSpacing: "-0.005em" }}>
                  Where does your water come from?
                </span>
                <ChevronDown className="lc-arrow" size={18} style={{ color: "#42954B", flexShrink: 0 }} aria-hidden />
              </summary>
              <div
                className="lc-body p-5 md:p-6 grid lg:grid-cols-12 gap-6 lg:gap-10"
                style={{
                  background: "#FAFAF7",
                  border: "1px solid rgba(12,42,64,0.10)",
                  borderLeft: "4px solid #42954B",
                  borderTop: "none",
                  borderBottomLeftRadius: 4,
                  borderBottomRightRadius: 4,
                }}
              >
                <div className="lg:col-span-4">
                  <p className="font-light leading-relaxed" style={{ fontSize: "0.95rem", color: "rgba(26,31,37,0.72)" }}>
                    Borehole, river intake, dam, spring, catchment or municipal.
                    Every source other than a small metered municipal connection
                    triggers a duty under the National Water Act — before a drop
                    is ever treated.
                  </p>
                </div>

                <div className="lg:col-span-8 space-y-4">
                  <StageBlock
                    label="The law"
                    body={<>
                      <strong>National Water Act 36 of 1998 Section 21</strong> lists
                      eleven water uses (a–k). The two that catch every commercial
                      site are <strong>Section 21(a) taking water from a water resource</strong>{" "}
                      and <strong>Section 21(b) storing water</strong>. Section 22 states that
                      &ldquo;<em>a person may only use water</em>&rdquo; if that use is
                      permissible under Schedule 1 (small domestic), is an
                      existing lawful use, is authorised by a <strong>General
                      Authorisation under Section 39</strong>, or is covered by a
                      site-specific <strong>Water Use Licence</strong>.
                    </>}
                  />
                  <StageBlock
                    label="What you must do"
                    body={<>
                      Register the abstraction on the DWS <strong>e-WULAAS</strong>{" "}
                      portal. Fit a functional meter to every registered point.
                      Log the volumetric reading monthly and submit the return on
                      the interval prescribed in your GA or WUL — typically
                      quarterly. Track the GA renewal or WUL validity on the
                      compliance calendar (WULs typically 5 &ndash; 40 years, GAs are
                      republished periodically in the Government Gazette).
                    </>}
                  />
                  <StageBlock
                    label="If you don&rsquo;t"
                    body={<>
                      Unregistered abstraction is an offence under <strong>NWA Section 151</strong> —
                      fine or imprisonment on conviction. DWS can also issue a{" "}
                      <strong>directive under Section 19</strong> requiring immediate cessation
                      of abstraction until the position is regularised — trading
                      can be forced to stop while the paperwork is being sorted.
                    </>}
                  />
                </div>
              </div>
            </details>

            {/* STAGE 2 — USE (collapsible banner) */}
            <details data-collapse data-testid="lifecycle-stage-use">
              <summary
                className="flex items-center gap-4 px-5 py-4 md:px-6 md:py-5"
                style={{
                  background: "#FAFAF7",
                  border: "1px solid rgba(12,42,64,0.10)",
                  borderLeft: "4px solid #1FB6C1",
                  borderRadius: 4,
                }}
              >
                <span className="font-mono" style={{ fontSize: "1.3rem", fontWeight: 700, color: "#1FB6C1", letterSpacing: "-0.02em", lineHeight: 1, flexShrink: 0 }}>
                  02
                </span>
                <span className="font-mono uppercase hidden md:inline-block md:w-36" style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "#1FB6C1", fontWeight: 700, flexShrink: 0 }}>
                  Stage · use
                </span>
                <span className="hidden md:inline" style={{ color: "rgba(12,42,64,0.25)" }}>·</span>
                <span className="font-display flex-1" style={{ fontSize: "1.02rem", fontWeight: 600, color: "#0C2A40", letterSpacing: "-0.005em" }}>
                  What do you actually use the water for?
                </span>
                <ChevronDown className="lc-arrow" size={18} style={{ color: "#1FB6C1", flexShrink: 0 }} aria-hidden />
              </summary>
              <div
                className="lc-body p-5 md:p-6 grid lg:grid-cols-12 gap-6 lg:gap-10"
                style={{
                  background: "#FAFAF7",
                  border: "1px solid rgba(12,42,64,0.10)",
                  borderLeft: "4px solid #1FB6C1",
                  borderTop: "none",
                  borderBottomLeftRadius: 4,
                  borderBottomRightRadius: 4,
                }}
              >
                <div className="lg:col-span-4">
                  <p className="font-light leading-relaxed" style={{ fontSize: "0.95rem", color: "rgba(26,31,37,0.72)" }}>
                    This is where most lodges get the compliance question wrong.
                    The purpose determines the standard — and lodges consistently
                    <strong> underestimate</strong> which purpose their guests
                    actually put the water to.
                  </p>
                </div>

                <div className="lg:col-span-8 space-y-4">
                  {/* Mythbuster callout */}
                  <div
                    className="p-5"
                    style={{
                      background: "rgba(184,92,56,0.06)",
                      border: "1px solid rgba(184,92,56,0.35)",
                      borderRadius: 4,
                    }}
                    data-testid="lifecycle-bathing-myth"
                  >
                    <div className="font-mono uppercase mb-2" style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "#B85C38", fontWeight: 700 }}>
                      Myth · &ldquo;our guests don&rsquo;t drink the water&rdquo;
                    </div>
                    <p className="font-light leading-relaxed" style={{ fontSize: "0.94rem", color: "#1A1F25" }}>
                      In law, the tap in the villa is a drinking-water outlet the
                      moment any of the following happens: a guest{" "}
                      <strong>brushes their teeth</strong>, <strong>rinses their
                      mouth in the shower</strong>, <strong>fills the kettle for
                      coffee</strong>, <strong>makes ice</strong>, <strong>washes
                      salad</strong>, <strong>boils pasta</strong>, or the
                      housekeeping team <strong>refills a bedside carafe</strong>.
                      Boiling does not remove nitrate, heavy metals or
                      disinfection by-products. &ldquo;They only bath in it&rdquo; is not a
                      defence — showering also delivers aerosolised chemical
                      exposure (WHO 2022, Section 6.3). Any water reaching mouth, skin
                      or food is judged against <strong>SANS 241:2015</strong>.
                    </p>
                  </div>

                  <StageBlock
                    label="Drinking, cooking, brushing, ice, showering — SANS 241:2015"
                    body={<>
                      SANS 241 defines four determinand classes —{" "}
                      <strong>acute health, chronic health, aesthetic and
                      operational</strong> — across microbiological and chemical
                      tables. The <strong>point of compliance is the point of
                      consumption</strong> (SANS 241:2015 Section 5.6.2) — the kitchen
                      tap and the villa basin, not the plant outlet. Free
                      chlorine that leaves the plant at 0.6 mg/L can sit at
                      0.18 mg/L at the far villa after a summer reticulation.
                      The plant may pass; the villa fails; the audit names the
                      villa. Duty is imposed by <strong>WSA Section 25</strong> and
                      monitored under <strong>Regulation R982 of 2001</strong>.
                    </>}
                  />

                  <StageBlock
                    label="Testing regime for a commercial site"
                    body={<>
                      • <strong>Monthly bacteriology</strong> — E.&nbsp;coli,
                      total coliforms, HPC — at a minimum of four representative
                      outlets, including the furthest point in the reticulation.
                      <br />• <strong>Quarterly operational chemistry</strong> —
                      free chlorine residual, conductivity, pH, turbidity.
                      <br />• <strong>Annual full SANS 241 panel</strong> —
                      heavy metals, fluoride, nitrate, disinfection by-products.
                      <br />• <strong>SANAS-accredited lab throughout</strong> —
                      field test-strips and non-accredited results are not
                      admissible in an audit. Records held for{" "}
                      <strong>at least three years</strong> (R982 of 2001).
                    </>}
                  />

                  <StageBlock
                    label="Pools, spas, plunge pools — different regime"
                    body={<>
                      Bathing water is regulated separately from drinking water
                      under the <strong>National Health Act 61 of 2003</strong>{" "}
                      via <strong>Government Notice R.943 of 2013</strong>{" "}
                      (National Environmental Health Norms &amp; Standards),
                      benchmarked against <strong>WHO 2006 Guidelines Vol. 2</strong>
                      &nbsp;— because the exposure route is dermal absorption and
                      inhalation, not ingestion. Applying SANS 241 to pool water
                      is regulatorily incorrect. Daily operator checks (free
                      chlorine, pH, turbidity), monthly microbiological sample
                      per pool, biannual chemical audit — recorded and produced
                      on inspection by the municipal Environmental Health
                      Practitioner.
                    </>}
                  />

                  <StageBlock
                    label="Irrigation & fire water"
                    body={<>
                      Non-food-contact <strong>irrigation</strong> follows the
                      DWA / DAFF <em>Water Quality Guidelines for Agricultural
                      Use — Irrigation</em> — less stringent than SANS 241 but
                      still enforceable where downstream contamination is
                      possible. <strong>Fire-water reserves and pumps</strong>{" "}
                      are certified under <strong>SANS 10090</strong> — reserve
                      size, weekly run-test log and annual certification are
                      insurance-mandatory on every commercial site.
                    </>}
                  />
                </div>
              </div>
            </details>

            {/* STAGE 3 — WASTEWATER (collapsible banner) */}
            <details data-collapse data-testid="lifecycle-stage-wastewater">
              <summary
                className="flex items-center gap-4 px-5 py-4 md:px-6 md:py-5"
                style={{
                  background: "#FAFAF7",
                  border: "1px solid rgba(12,42,64,0.10)",
                  borderLeft: "4px solid #1D6A8C",
                  borderRadius: 4,
                }}
              >
                <span className="font-mono" style={{ fontSize: "1.3rem", fontWeight: 700, color: "#1D6A8C", letterSpacing: "-0.02em", lineHeight: 1, flexShrink: 0 }}>
                  03
                </span>
                <span className="font-mono uppercase hidden md:inline-block md:w-36" style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "#1D6A8C", fontWeight: 700, flexShrink: 0 }}>
                  Stage · discharge
                </span>
                <span className="hidden md:inline" style={{ color: "rgba(12,42,64,0.25)" }}>·</span>
                <span className="font-display flex-1" style={{ fontSize: "1.02rem", fontWeight: 600, color: "#0C2A40", letterSpacing: "-0.005em" }}>
                  What happens to the water once you&rsquo;re done with it?
                </span>
                <ChevronDown className="lc-arrow" size={18} style={{ color: "#1D6A8C", flexShrink: 0 }} aria-hidden />
              </summary>
              <div
                className="lc-body p-5 md:p-6 grid lg:grid-cols-12 gap-6 lg:gap-10"
                style={{
                  background: "#FAFAF7",
                  border: "1px solid rgba(12,42,64,0.10)",
                  borderLeft: "4px solid #1D6A8C",
                  borderTop: "none",
                  borderBottomLeftRadius: 4,
                  borderBottomRightRadius: 4,
                }}
              >
                <div className="lg:col-span-4">
                  <p className="font-light leading-relaxed" style={{ fontSize: "0.95rem", color: "rgba(26,31,37,0.72)" }}>
                    Effluent from kitchens, ablutions, laundries, package
                    treatment plants and process water is a{" "}
                    <em>water use</em> in its own right under the NWA — the
                    moment it leaves the property, another set of duties opens.
                  </p>
                </div>

                <div className="lg:col-span-8 space-y-4">
                <StageBlock
                  label="The law"
                  body={<>
                    <strong>NWA Section 21(f)</strong> — discharging waste or
                    water-containing-waste into a water resource — and{" "}
                    <strong>Section 21(g)</strong> — disposing of waste in a manner
                    that may detrimentally impact a water resource — are
                    themselves listed water uses. Authorisation is either a{" "}
                    <strong>General Authorisation under Section 39</strong> published
                    as <strong>Government Notice 665 of 2013 (GG 36820)</strong>{" "}
                    or, if you exceed the GA threshold or the catchment is
                    designated sensitive, a site-specific{" "}
                    <strong>Water Use Licence</strong>.
                  </>}
                />

                {/* GA limits table — verbatim from GN 665 of 2013 */}
                <div
                  className="rounded-sm overflow-hidden"
                  style={{ border: "1px solid rgba(12,42,64,0.10)", background: "#FFFFFF" }}
                >
                  <div
                    className="px-4 py-2.5 font-mono uppercase"
                    style={{
                      background: "#0C2A40", color: "#F5F7FA",
                      fontSize: "0.62rem", letterSpacing: "0.2em", fontWeight: 700,
                    }}
                  >
                    GN 665 of 2013 · effluent limits (verbatim)
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left" style={{ fontSize: "0.86rem", minWidth: 520 }}>
                      <thead>
                        <tr style={{ background: "#FAFAF7", color: "rgba(12,42,64,0.65)" }}>
                          <th className="px-4 py-2 font-mono uppercase" style={{ fontSize: "0.58rem", letterSpacing: "0.18em" }}>Determinand</th>
                          <th className="px-3 py-2 font-mono uppercase text-right" style={{ fontSize: "0.58rem", letterSpacing: "0.18em" }}>General limit</th>
                          <th className="px-3 py-2 font-mono uppercase text-right" style={{ fontSize: "0.58rem", letterSpacing: "0.18em" }}>Special limit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ["COD",              "≤ 75 mg/L",  "≤ 30 mg/L"],
                          ["Suspended solids", "≤ 25 mg/L",  "≤ 10 mg/L"],
                          ["Faecal coliforms", "≤ 1 000 /100 mL", "0 /100 mL"],
                          ["Ammonia (as N)",   "≤ 6 mg/L",   "≤ 2 mg/L"],
                          ["Nitrate (as N)",   "≤ 15 mg/L",  "≤ 1.5 mg/L"],
                          ["Ortho-phosphate (as P)", "≤ 10 mg/L", "≤ 1 mg/L (median)"],
                          ["pH",               "5.5 – 9.5",  "5.5 – 7.5"],
                        ].map(([d, g, s]) => (
                          <tr key={d} style={{ borderTop: "1px solid rgba(12,42,64,0.06)" }}>
                            <td className="px-4 py-2 font-display" style={{ fontWeight: 600, color: "#0C2A40" }}>{d}</td>
                            <td className="px-3 py-2 text-right font-mono" style={{ color: "rgba(12,42,64,0.75)" }}>{g}</td>
                            <td className="px-3 py-2 text-right font-mono" style={{ color: "#B85C38", fontWeight: 600 }}>{s}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div
                    className="px-4 py-2.5 font-light"
                    style={{
                      background: "#FAFAF7",
                      fontSize: "0.78rem",
                      color: "rgba(26,31,37,0.65)",
                      borderTop: "1px solid rgba(12,42,64,0.08)",
                    }}
                  >
                    General Limit applies to standard catchments. Special Limit
                    applies to sensitive or listed catchments (published per
                    catchment by DWS). Above <strong>2 000 m³/day</strong>, the
                    GA falls away and a site-specific Water Use Licence is
                    mandatory.
                  </div>
                </div>

                <StageBlock
                  label="What you must do"
                  body={<>
                    Monthly compliance sample against the GA limits by a
                    SANAS-accredited lab · monthly / quarterly volumetric
                    return via <strong>e-WULAAS</strong> · sludge disposal
                    manifest for every removal · maintenance and calibration
                    records on all dosing and disinfection kit · non-conformance
                    report and corrective-action workflow on every out-of-limit
                    result.
                  </>}
                />
                <StageBlock
                  label="If you don&rsquo;t"
                  body={<>
                    Non-compliant discharge is a criminal offence under{" "}
                    <strong>NWA Section 151(1)(i)</strong> — on conviction, fine or
                    imprisonment up to five years for a first offence, ten
                    years for a repeat. DWS can also serve a{" "}
                    <strong>directive under Section 53</strong> requiring the
                    discharge to stop until remediated. Municipal Environmental
                    Health inspectors can prosecute under the National Health
                    Act in parallel — two regulators, two dockets.
                  </>}
                />
                </div>
              </div>
            </details>
          </div>

          <p className="mt-8 font-light" style={{ fontSize: "0.86rem", color: "rgba(26,31,37,0.55)" }}>
            Instruments cited in this section: National Water Act 36 of 1998
            (Sections 19, 21, 22, 39, 53, 151) · Water Services Act 108 of 1997 (Section 25) ·
            Regulation R982 of 2001 (Compulsory National Standards) · SANS
            241:2015 (Drinking Water) · Government Notice R.943 of 2013 (National
            Environmental Health Norms &amp; Standards, under NHA 61 of 2003) ·
            Government Notice 665 of 2013 (GG 36820, GA for waste discharge) ·
            SANS 10090 (Community Protection Against Fire). WHO 2022 Guidelines
            for Drinking-water Quality and WHO 2006 Guidelines for Safe
            Recreational Water (Vol. 2) apply as international benchmarks.
          </p>
        </div>
      </section>

      {/* DUTIES — heading on top, grid below (per operator direction) */}
      <section className="px-6 md:px-12 lg:px-20 py-14 md:py-20" style={{ background: "#FAFAF7" }} data-testid="duties">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="font-display mb-3" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)", lineHeight: 1.15, letterSpacing: "-0.015em", fontWeight: 500, color: "#0C2A40" }}>
            Statutory duties of a Water Services Intermediary.
          </h2>
          <p className="font-light leading-relaxed mb-10 max-w-3xl" style={{ fontSize: "0.98rem", color: "rgba(26,31,37,0.7)" }}>
            The recurring duties imposed on every water services intermediary
            in South Africa under the Water Services Act 108 of 1997 (Section 25),
            the Compulsory National Standards (Regulation R982 of 2001) and
            SANS 241:2015. Interval and scope are set by the applicable
            instrument for each item.
          </p>
          <div className="space-y-3" data-testid="duties-banners">
            {[
              {
                title: "Water quality monitoring",
                body: "Microbiological and chemical analysis by a SANAS-accredited laboratory, sampled at the point of consumption at intervals set by the WSA or by SANS 241:2015 Annex B risk-based sampling.",
                ref: "WSA Section 25(1)(a) · R982 of 2001 Section 5(2) · SANS 241 Annex B",
              },
              {
                title: "Treatment and disinfection",
                body: "The water services intermediary must provide water that meets the numerical limits of SANS 241:2015. Residual disinfectant is a listed operational determinand (Table 2).",
                ref: "SANS 241:2015 Section 4 & Table 2 · WSA Section 25(1)(a)",
              },
              {
                title: "On-going monitoring of critical points",
                body: "Continuous or scheduled observation of pressure, flow, residual chlorine, UV intensity, turbidity and consumption, in line with the site's water safety plan.",
                ref: "SANS 241:2015 Section 4.2 (water quality management) · WSA Section 25",
              },
              {
                title: "Corrective action on exceedances",
                body: "Every exceedance of a numerical limit must be investigated, root-caused, corrected and verified. The record is retained in the site's compliance file.",
                ref: "R982 of 2001 Section 5(3) · SANS 241:2015 Section 5.5",
              },
              {
                title: "Water use return (abstraction & discharge)",
                body: "Volumetric return of water abstracted or discharged, submitted to DWS via the e-WULAAS portal on the interval prescribed in the General Authorisation or Water Use Licence.",
                ref: "NWA 36 of 1998 Section 26 & Section 35 · GA / WUL conditions",
              },
              {
                title: "Operator training and competency",
                body: "Documented training, competency assessment and re-training of every operator, technician and maintenance contractor working on the water services works.",
                ref: "OHS Act 85 of 1993 Section 8 · General Safety Regulations",
              },
            ].map((d) => (
              <details key={d.title} data-collapse data-testid={`duty-${d.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <summary
                  className="flex items-center gap-4 px-5 py-4 md:px-6 md:py-5"
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid rgba(12,42,64,0.10)",
                    borderLeft: "4px solid #1FB6C1",
                    borderRadius: 4,
                  }}
                >
                  <span
                    className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: "rgba(31,182,193,0.12)", color: "#1FB6C1" }}
                  >
                    <BookMarked size={15} aria-hidden />
                  </span>
                  <span className="font-display flex-1" style={{ fontSize: "1.02rem", fontWeight: 600, color: "#0C2A40", letterSpacing: "-0.005em" }}>
                    {d.title}
                  </span>
                  <ChevronDown className="lc-arrow" size={18} style={{ color: "#1FB6C1", flexShrink: 0 }} aria-hidden />
                </summary>
                <div
                  className="lc-body p-5 md:p-6"
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid rgba(12,42,64,0.10)",
                    borderLeft: "4px solid #1FB6C1",
                    borderTop: "none",
                    borderBottomLeftRadius: 4,
                    borderBottomRightRadius: 4,
                  }}
                >
                  <p className="font-light leading-relaxed" style={{ fontSize: "0.94rem", color: "rgba(26,31,37,0.78)", maxWidth: "80ch" }}>
                    {d.body}
                  </p>
                  <div
                    className="mt-4 pt-3 font-mono uppercase"
                    style={{ fontSize: "0.6rem", letterSpacing: "0.18em", color: "rgba(12,42,64,0.55)", borderTop: "1px dashed rgba(12,42,64,0.14)", fontWeight: 700 }}
                  >
                    {d.ref}
                  </div>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* EVIDENCE PACK — compliance file requirements */}
      <section className="px-6 md:px-12 lg:px-20 py-14 md:py-20" style={{ background: "#FFFFFF", color: "#0C2A40" }} data-testid="evidence-pack">
        <div className="max-w-[1500px] mx-auto">
          <h2 className="font-display mb-3" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)", lineHeight: 1.15, letterSpacing: "-0.015em", fontWeight: 500, color: "#0C2A40" }}>
            Compliance file requirements.
          </h2>
          <p className="font-light leading-relaxed mb-10 max-w-3xl" style={{ fontSize: "0.98rem", color: "rgba(26,31,37,0.7)" }}>
            The documents that constitute a defensible compliance file for a
            commercial water services site. Each item is annotated with the
            statute, regulation, standard or gazetted authorisation that
            imposes the record-keeping obligation.
          </p>
          <div className="space-y-3" data-testid="evidence-banners">
            {EVIDENCE.map((e) => {
              const Icon = e.icon;
              return (
                <details key={e.title} data-collapse data-testid={`evidence-${e.title.replace(/\s+/g, "-").toLowerCase()}`}>
                  <summary
                    className="flex items-center gap-4 px-5 py-4 md:px-6 md:py-5"
                    style={{
                      background: "#FAFAF7",
                      border: "1px solid rgba(12,42,64,0.10)",
                      borderLeft: `4px solid ${e.accent}`,
                      borderRadius: 4,
                    }}
                  >
                    <span
                      className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                      style={{ background: `${e.accent}1F`, color: e.accent }}
                    >
                      <Icon size={15} aria-hidden />
                    </span>
                    <span className="font-display flex-1 md:flex-none md:w-96" style={{ fontSize: "1.02rem", fontWeight: 700, color: "#0C2A40", letterSpacing: "-0.005em" }}>
                      {e.title}
                    </span>
                    <span className="hidden md:inline" style={{ color: "rgba(12,42,64,0.25)", flexShrink: 0 }}>·</span>
                    <span className="hidden md:inline-block font-mono uppercase md:w-64" style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: e.accent, fontWeight: 700, flexShrink: 0 }}>
                      {e.eyebrow}
                    </span>
                    <span className="flex-1" />
                    <ChevronDown className="lc-arrow" size={18} style={{ color: e.accent, flexShrink: 0 }} aria-hidden />
                  </summary>
                  <div
                    className="lc-body p-5 md:p-6"
                    style={{
                      background: "#FAFAF7",
                      border: "1px solid rgba(12,42,64,0.10)",
                      borderLeft: `4px solid ${e.accent}`,
                      borderTop: "none",
                      borderBottomLeftRadius: 4,
                      borderBottomRightRadius: 4,
                    }}
                  >
                    <div className="md:hidden mb-3 font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: e.accent, fontWeight: 700 }}>
                      {e.eyebrow}
                    </div>
                    <p className="font-light leading-relaxed" style={{ fontSize: "0.94rem", color: "rgba(26,31,37,0.78)", maxWidth: "80ch" }}>
                      {e.lede}
                    </p>
                    {e.ref && (
                      <div
                        className="mt-4 pt-3 font-mono uppercase"
                        style={{ fontSize: "0.6rem", letterSpacing: "0.18em", color: "rgba(12,42,64,0.55)", borderTop: "1px dashed rgba(12,42,64,0.14)", fontWeight: 700 }}
                      >
                        {e.ref}
                      </div>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        </div>
      </section>

      {/* PATH TO COMPLIANCE — 4-step journey */}
      <section
        className="px-6 md:px-12 lg:px-20 py-14 md:py-20"
        style={{ background: "#0C2A40", color: "#F5F7FA" }}
        data-testid="compliance-path"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5"
            style={{
              background: "rgba(31,182,193,0.14)",
              border: "1px solid rgba(31,182,193,0.35)",
              color: "#1FB6C1",
              fontSize: "0.64rem", letterSpacing: "0.22em",
              textTransform: "uppercase", fontWeight: 700, borderRadius: 999,
            }}
          >
            <FileCheck2 size={12} aria-hidden /> From exposed to audit-ready
          </div>
          <h2 className="font-display mb-4 max-w-3xl" style={{
            fontSize: "clamp(1.8rem, 3.4vw, 2.8rem)",
            lineHeight: 1.1, letterSpacing: "-0.018em", fontWeight: 500, color: "#F5F7FA",
          }}>
            Your path to a defensible compliance file.<br />
            <span style={{ color: "#1FB6C1" }}>Four steps. Four to eight weeks.</span>
          </h2>
          <p className="font-light leading-relaxed mb-10 max-w-3xl" style={{ fontSize: "1rem", color: "rgba(245,247,250,0.75)" }}>
            We do not sell a plant on day one. We start with a documented gap
            analysis and a fixed-price remediation plan — and we only quote the
            equipment side once we know exactly where the paper trail is broken.
          </p>

          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="compliance-path-steps">
            {[
              {
                n: "01",
                w: "Week 1",
                t: "Gap analysis on site",
                b: "We come on site, sample the feed and every representative outlet, photograph the plant and the abstraction works, and list every duty under WSA, NWA, SANS 241, SANS 10090 and your sector-specific obligations. One document. No surprises.",
              },
              {
                n: "02",
                w: "Week 2",
                t: "Fixed-price remediation plan",
                b: "Every non-conformance is priced against a specific fix. You decide what to close now, what to schedule, and what to accept. Nothing is bundled or upsold — every line item is defensible to your auditor and your board.",
              },
              {
                n: "03",
                w: "Week 3 – 6",
                t: "Remediation & baseline sample",
                b: "Plant fixes, sampling programme installed, operator round-sheets configured, DWS registrations lodged, fire-water certification arranged. The first full SANS 241 panel becomes your baseline record.",
              },
              {
                n: "04",
                w: "Week 4+",
                t: "HidroVerse Care — monthly",
                b: "Monthly SANAS sampling, archived certificates in the client portal, NCR &amp; CAPA workflow, quarterly abstraction returns, annual fire certification. When the auditor arrives, we produce the file inside an hour and stand beside you while they read it.",
              },
            ].map((s) => (
              <li key={s.n}
                className="p-5 flex flex-col"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  borderTop: "3px solid #1FB6C1",
                  borderRadius: 4,
                }}
              >
                <div className="flex items-baseline justify-between mb-4">
                  <div
                    className="font-mono"
                    style={{ fontSize: "1.6rem", fontWeight: 700, color: "#1FB6C1", letterSpacing: "-0.02em" }}
                  >
                    {s.n}
                  </div>
                  <div className="font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: "rgba(245,247,250,0.55)" }}>
                    {s.w}
                  </div>
                </div>
                <div className="font-display mb-2" style={{ fontSize: "1.02rem", fontWeight: 700, letterSpacing: "-0.005em", color: "#F5F7FA" }}>
                  {s.t}
                </div>
                <p className="font-light leading-relaxed" style={{ fontSize: "0.9rem", color: "rgba(245,247,250,0.72)" }}
                  dangerouslySetInnerHTML={{ __html: s.b }}
                />
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* STANDARDS BADGE STRIP */}
      <section className="bg-[#fbfbfa] py-10 md:py-12 px-6 md:px-12 lg:px-20 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
        <div className="max-w-[1500px] mx-auto flex flex-wrap items-center justify-between gap-6">
          <div className="eyebrow text-black/55" style={{ fontSize: "0.6rem", letterSpacing: "0.28em" }}>
            Designed to · Documented to · Defensible by
          </div>
          <div className="badge-strip">
            {STANDARDS_BADGES.map((b) => <span key={b} className="badge light">{b}</span>)}
          </div>
        </div>
      </section>

      {/* COMPLIANCE FAQ — moved to bottom, renamed, custom intro */}
      <div data-testid="compliance-faq">
        <FAQ
          title="Compliance FAQ."
          intro="The questions lodge managers, developers, body-corporate trustees and municipal engineers ask most often when trying to understand their statutory duties under the Water Services Act 108 of 1997, the National Water Act 36 of 1998 and SANS 241:2015. Can't see yours? Send us a brief — a HidroVerse compliance engineer replies within one business day."
          faqs={COMPLIANCE_GUIDE}
        />
      </div>

      {/* CTA */}
      <section className="cta-strip">
        <div className="csi">
          <div>
            <h3>Talk to our compliance specialists.</h3>
            <p className="csi-meta">
              For personalised assistance and solutions, contact our specialists.
            </p>
          </div>
          <Link to="/contact" className="hv-magnetic" style={{ justifyContent: "center" }}>
            Contact us <span aria-hidden>→</span>
          </Link>
        </div>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------
//  StageBlock — small labelled block used inside each lifecycle stage
//  card (The law · What you must do · If you don't).
// -----------------------------------------------------------------------
function StageBlock({ label, body }) {
  return (
    <div>
      <div
        className="font-mono uppercase mb-1.5"
        style={{ fontSize: "0.58rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.55)", fontWeight: 700 }}
      >
        {typeof label === "string" ? (
          <span dangerouslySetInnerHTML={{ __html: label }} />
        ) : label}
      </div>
      <div
        className="font-light leading-relaxed"
        style={{ fontSize: "0.94rem", color: "rgba(26,31,37,0.78)" }}
      >
        {body}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
//  PillarList — labelled bullet list inside the "Three pillars" cards.
// -----------------------------------------------------------------------
function PillarList({ label, items, tint }) {
  return (
    <div className="mb-4">
      <div
        className="font-mono uppercase mb-2"
        style={{ fontSize: "0.58rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.55)", fontWeight: 700 }}
      >
        {label}
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2.5 items-start">
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 5, height: 5, borderRadius: 999,
                background: tint, marginTop: 8, flexShrink: 0,
              }}
            />
            <span
              className="font-light leading-relaxed"
              style={{ fontSize: "0.86rem", color: "rgba(26,31,37,0.78)" }}
            >
              {it}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

